/**
 * AI内容智能识别与分类模块
 * 用于识别AI回答内容的类型，并路由到对应的处理策略
 */

export type AIContentType =
  | 'full-page'      // 完整HTML页面
  | 'partial-html'   // 只有HTML结构无样式
  | 'text-only'      // 纯文本描述
  | 'mixed'          // 文字 + 代码片段
  | 'design-advice'  // 设计建议，无代码
  | 'unknown';

export interface ContentProfile {
  type: AIContentType;
  htmlBlock: string | null;
  codeFragments: string[];
  cssBlocks: string[];
  jsBlocks: string[];
  textHint: string;
  confidence: number;
  visualKeywords: string[];
}

/**
 * 提取代码块（支持 ```html 和直接HTML）
 */
function extractCodeBlocks(raw: string): string[] {
  const blocks: string[] = [];
  
  // 匹配 ```html ... ``` 格式（非贪婪匹配）
  const fenceRe = /```(?:html|jsx|tsx|htm|css|javascript|typescript)?\s*([\s\S]*?)```/gi;
  let m;
  while ((m = fenceRe.exec(raw)) !== null) {
    const code = m[1].trim();
    if (code) blocks.push(code);
  }
  
  // 匹配直接HTML（<!DOCTYPE...> 或 <html>...</html>）
  // 使用更宽松的正则，匹配从<!DOCTYPE或<html开始到</html>结束
  const directRe = /(<!DOCTYPE\s+html[\s\S]*?<\/html>|<html[\s\S]*?<\/html>)/i;
  const d = raw.match(directRe);
  if (d && d[0] && !blocks.some(b => b.includes(d[0]!.substring(0, 100)))) {
    blocks.push(d[0].trim());
  }
  
  return blocks;
}

/**
 * 检查是否是完整的HTML页面
 */
function isCompletePage(html: string): boolean {
  return html.includes('<!DOCTYPE') || 
         (html.includes('<html') && html.includes('</html>'));
}

/**
 * 提取页面类型关键词
 */
function extractPageType(text: string): string[] {
  const keywords: string[] = [];
  const patterns = [
    { keyword: 'login', regex: /登录|login|signin/i },
    { keyword: 'register', regex: /注册|register|signup/i },
    { keyword: 'landing', regex: /首页|landing|首页|欢迎页/i },
    { keyword: 'dashboard', regex: /仪表盘|dashboard|控制台/i },
    { keyword: 'form', regex: /表单|form/i },
    { keyword: 'table', regex: /表格|table|列表|list/i },
    { keyword: 'card', regex: /卡片|card/i },
    { keyword: 'game', regex: /游戏|game/i },
    { keyword: 'animation', regex: /动画|animation/i },
  ];
  
  for (const { keyword, regex } of patterns) {
    if (regex.test(text)) {
      keywords.push(keyword);
    }
  }
  
  return keywords;
}

/**
 * 分类AI回答内容
 */
export function classifyContent(raw: string): ContentProfile {
  const codeBlocks = extractCodeBlocks(raw);
  const fullHtml = codeBlocks.find(isCompletePage) || null;
  const hasMarkup = codeBlocks.some(b => /<(div|button|form|input|h\d|p|nav|header|section|canvas|svg)/i.test(b));
  
  // 去除代码后的纯文字
  const textOnly = raw.replace(/```[\s\S]*?```/g, '').trim();
  const visualKeywords = extractPageType(raw);
  
  // 决策树
  // T1: 完整页面
  if (fullHtml && isCompletePage(fullHtml)) {
    return {
      type: 'full-page',
      htmlBlock: fullHtml,
      codeFragments: codeBlocks,
      cssBlocks: [],
      jsBlocks: [],
      textHint: textOnly,
      confidence: 0.95,
      visualKeywords,
    };
  }
  
  // T2: 部分HTML（有结构无完整页面）
  if (codeBlocks.length > 0 && hasMarkup) {
    const hasCss = codeBlocks.some(b => b.includes('<style>') || /\{[^}]+\}/.test(b));
    if (!hasCss && !fullHtml) {
      return {
        type: 'partial-html',
        htmlBlock: codeBlocks[0],
        codeFragments: codeBlocks,
        cssBlocks: [],
        jsBlocks: [],
        textHint: textOnly,
        confidence: 0.8,
        visualKeywords,
      };
    }
  }
  
  // T4: 混合内容（有代码也有文字说明）
  if (codeBlocks.length > 0 && textOnly.length > 20) {
    return {
      type: 'mixed',
      htmlBlock: codeBlocks[0],
      codeFragments: codeBlocks,
      cssBlocks: [],
      jsBlocks: [],
      textHint: textOnly,
      confidence: 0.75,
      visualKeywords,
    };
  }
  
  // T5: 设计建议
  if (/建议|主题|风格|配色|布局|应当|可以|推荐|tone|style|color|theme/i.test(raw) && codeBlocks.length === 0) {
    return {
      type: 'design-advice',
      htmlBlock: null,
      codeFragments: [],
      cssBlocks: [],
      jsBlocks: [],
      textHint: textOnly,
      confidence: 0.7,
      visualKeywords,
    };
  }
  
  // T3: 纯文本描述
  if (codeBlocks.length === 0 && textOnly.length > 10) {
    return {
      type: 'text-only',
      htmlBlock: null,
      codeFragments: [],
      cssBlocks: [],
      jsBlocks: [],
      textHint: textOnly,
      confidence: 0.6,
      visualKeywords,
    };
  }
  
  // 未知类型
  return {
    type: 'unknown',
    htmlBlock: null,
    codeFragments: codeBlocks,
    cssBlocks: [],
    jsBlocks: [],
    textHint: textOnly,
    confidence: 0.3,
    visualKeywords,
  };
}

/**
 * 获取内容类型的中文标签
 */
export function getContentTypeLabel(type: AIContentType): string {
  const labels: Record<AIContentType, string> = {
    'full-page': '原样渲染',
    'partial-html': 'AI补全样式',
    'text-only': 'AI生成',
    'mixed': 'AI组合',
    'design-advice': '设计建议',
    'unknown': '未知内容',
  };
  return labels[type] || '未知';
}

/**
 * 获取内容类型的颜色标识
 */
export function getContentTypeColor(type: AIContentType): string {
  const colors: Record<AIContentType, string> = {
    'full-page': 'bg-green-100 text-green-800',
    'partial-html': 'bg-blue-100 text-blue-800',
    'text-only': 'bg-purple-100 text-purple-800',
    'mixed': 'bg-orange-100 text-orange-800',
    'design-advice': 'bg-yellow-100 text-yellow-800',
    'unknown': 'bg-gray-100 text-gray-800',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}
